import { ROTATABLE_SYMBOL_NAMES, SYMBOL_SHAPES } from './geometry';

/**
 * Ported from frontend/shared/src/commonMain/kotlin/sk/myslikrok/shared/Symbols/SymbolParser.kt.
 * A change to the Kotlin changes this file in the same commit — client code manifest §2a.
 *
 * Turns an API token into a `SymbolToken`. Never throws: an unparseable token is `unknown`, which the
 * renderer knows how to show.
 */
export type SymbolToken =
  /** A named symbol, optionally rotated and mirrored, optionally repeated inside a matrix cell. */
  | { kind: 'named'; name: string; rotationDegrees: number; isMirrored: boolean; count: number }
  /** A single digit, drawn as a numeral rather than a symbol. */
  | { kind: 'digit'; value: number }
  /** A grid written inline, rows joined by `/` — the form option tokens use. */
  | { kind: 'grid'; rows: string[] }
  /**
   * Anything the client does not recognise.
   *
   * Kept as a case rather than thrown away: challenges are code, so a backend release can add a symbol
   * before a client release ships. The renderer draws a neutral placeholder — never blank, never a crash,
   * and never a *different* symbol, which would silently change the task.
   */
  | { kind: 'unknown'; raw: string };

const VALID_ROTATIONS = new Set([0, 90, 180, 270]);

const known = (name: string): boolean => name in SYMBOL_SHAPES || ROTATABLE_SYMBOL_NAMES.has(name);

export function parseSymbol(raw: string): SymbolToken {
  const token = raw.trim();

  if (token.length === 0) return { kind: 'unknown', raw };

  if (token.includes('/')) {
    const rows = token.split('/');

    return rows.length >= 2 && rows.length <= 5 && rows.every((row: string): boolean => row.length > 0)
      ? { kind: 'grid', rows }
      : { kind: 'unknown', raw };
  }

  if (token.length === 1 && token >= '0' && token <= '9') {
    return { kind: 'digit', value: Number(token) };
  }

  const parts = token.split(':');
  const [name, count] = splitCount(parts[0]);

  if (!known(name)) return { kind: 'unknown', raw };

  let rotationDegrees = 0;
  let isMirrored = false;

  for (const modifier of parts.slice(1)) {
    if (modifier === 'mirrored') {
      isMirrored = true;
      continue;
    }

    const asNumber = /^\d+$/.test(modifier) ? Number(modifier) : NaN;

    if (VALID_ROTATIONS.has(asNumber)) {
      rotationDegrees = asNumber;
      continue;
    }

    return { kind: 'unknown', raw };
  }

  return { kind: 'named', name, rotationDegrees, isMirrored, count };
}

/** `circlex2` is two circles in a matrix cell. The suffix is only a count when the base name is real. */
function splitCount(segment: string): [string, number] {
  const marker = segment.lastIndexOf('x');

  if (marker <= 0 || marker !== segment.length - 2) return [segment, 1];

  const count = Number(segment[segment.length - 1]);
  const base = segment.slice(0, marker);

  if (!Number.isInteger(count)) return [segment, 1];

  return count >= 1 && count <= 3 && base in SYMBOL_SHAPES ? [base, count] : [segment, 1];
}

export const gridColumnCount = (rows: string[]): number =>
  rows.reduce((widest: number, row: string): number => Math.max(widest, row.length), 0);

export const gridCellAt = (rows: string[], row: number, column: number): string => rows[row]?.[column] ?? '.';
